<template>
	<div
		class="summary__contents"
		:class="`is-sectionTitle-${sectionTitleStyle}`"
	>
		<div class="summary__section">
			<p class="summary__sectionTitle">{{ $t("Cargas permanentes") }}</p>

			<div class="summary__grid">
				<div class="summary__item">
					<div class="summary__itemLabel">
						{{ $t("Carga distribuída") }}
					</div>
					<div
						class="summary__itemValue"
						v-html="permanentDistributedSummaryContent"
					></div>
				</div>

				<div class="summary__item">
					<div class="summary__itemLabel">{{ $t("Carga pontual") }}</div>
					<div
						class="summary__itemValue"
						v-html="pointSummaryContent('permanent')"
					></div>
				</div>
			</div>
		</div>

		<div class="summary__section">
			<p class="summary__sectionTitle">{{ $t("Sobrecargas") }}</p>

			<div class="summary__grid">
				<div class="summary__item">
					<div class="summary__itemLabel">{{ $t("Sobrecargas") }}</div>
					<div class="summary__itemValue" v-html="deltaCfgSummaryContent"></div>
				</div>

				<div class="summary__item">
					<div class="summary__itemLabel">
						{{ $t("Carga distribuída") }}
					</div>
					<div
						class="summary__itemValue"
						v-html="liveDistributedSummaryContent"
					></div>
				</div>

				<div class="summary__item">
					<div class="summary__itemLabel">{{ $t("Carga pontual") }}</div>
					<div
						class="summary__itemValue"
						v-html="pointSummaryContent('live')"
					></div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import emitLifecycleEvents from "@/mixins/emitLifecycleEvents";

import BBtn from "@/components/base/BBtn.vue";
import BIcon from "@/components/base/BIcon/index.vue";

export default {
	mixins: [emitLifecycleEvents],
	components: {
		BBtn,
		BIcon,
	},
	props: {
		values: {
			type: Object,
			required: true,
		},
		sectionTitleStyle: {
			type: String,
			default: "block",
		},
	},
	computed: {
		...mapState("simulator", ["simulationOptions"]),
		...mapGetters("simulator", [
			"getOption",
			"spanDescriptions",
			"getMainSpanDescriptions",
			"consoleSpanDescriptions",
		]),

		permanentDistributedSummaryContent() {
			const {
				main,
				console,
				cfgMode,
			} = this.values.loads.permanent.distributed;
			let items;

			if (!cfgMode.isSpanBySpan) {
				if (!cfgMode.isMinMax) {
					items = [`${main[0].min} kN/m2`];
				} else {
					items = [`${main[0].min} - ${main[0].max} kN/m2`];
				}
			} else {
				if (!cfgMode.isMinMax) {
					items = [
						...console.map((val, idx) => {
							const label = this.consoleSpanDescriptions[idx].label;
							return `${this.$t(label)}: ${val.min} kN/m2`;
						}),
						...main.map((val, idx) => {
							const label = this.getMainSpanDescriptions(this.values.spans)[idx]
								.label;
							return `${this.$t(label)}: ${val.min} kN/m2`;
						}),
					];
				} else {
					items = [
						...console.map((val, idx) => {
							const label = this.consoleSpanDescriptions[idx].label;
							return `${this.$t(label)}: ${val.min} - ${val.max}  kN/m2`;
						}),
						...main.map((val, idx) => {
							const label = this.getMainSpanDescriptions(this.values.spans)[idx]
								.label;
							return `${this.$t(label)}: ${val.min} - ${val.max}  kN/m2`;
						}),
					];
				}
			}
			return items.map((item) => `<p>${item}</p>`).join("");
		},

		liveDistributedSummaryContent() {
			const { main, console, cfgMode } = this.values.loads.live.distributed;
			let items;

			if (!cfgMode.isSpanBySpan) {
				items = [`${main[0]} kN/m2`];
			} else {
				items = [
					...console.map((val, idx) => {
						const label = this.consoleSpanDescriptions[idx].label;
						return `${this.$t(label)}: ${val} kN/m2`;
					}),
					...main.map((val, idx) => {
						const label = this.getMainSpanDescriptions(this.values.spans)[idx]
							.label;
						return `${this.$t(label)}: ${val} kN/m2`;
					}),
				];
			}
			return items.map((item) => `<p>${item}</p>`).join("");
		},

		pointSummaryContent() {
			return (permanentOrlive) => {
				const groups = this.values.loads[permanentOrlive].point;

				const loadsStuffByIdx = [
					{ label: "P1", unit: "kN" },
					{ label: "P2", unit: "kN" },
					{ label: "L1", unit: "%" },
					{ label: "L2", unit: "%" },
				];

				return groups
					.map(({ targetSpans, loads }) => {
						const targetSpansStr = targetSpans
							.map((spanId) => {
								const { label } = this.spanDescriptions.find(
									({ id }) => id === spanId
								);
								return label;
							})
							.join(", ");

						const targetSpansParagraph = `<p>${targetSpansStr}</p>`;

						const loadsParagraphs = loads
							.map((load, idx) => {
								const { label, unit } = loadsStuffByIdx[idx];
								return `<p>${this.$t(label)} — ${load}${unit}</p>`;
							})
							.join("");

						return `
						${targetSpansParagraph}
						${loadsParagraphs}
					`;
					})
					.join("<br/>");
			};
		},

		deltaCfgSummaryContent() {
			const { cfgMode, deltas } = this.values.loads.live.distributed;

			if (cfgMode.deltaCfgOption !== "customized") {
				return this.getOption("liveDistributedDeltaCfg", cfgMode.deltaCfgOption)
					.label;
			} else {
				return deltas
					.map((delta, idx) => {
						return `<p>Ψ${idx} - ${delta}</p>`;
					})
					.join("");
			}
		},
	},
};
</script>

<style lang="scss"></style>
